import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Eye, IdCard, Plus, Search, UserMinus, UserPlus } from 'lucide-react'
import { EmptyRow, ErpTable, Td, Th } from '@/components/erp/primitives'
import { CursorPager } from '@/shared/ui/CursorPager'
import { Modal, Field } from '@/shared/ui/Modal'
import { ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { useCursorPage } from '@/shared/hooks/useCursorPage'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useCan } from '@/shared/auth/session'
import { dayKey, fmtDate, fmtNumber, humanise } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type {
  BeneficiaryContactResponse, BeneficiaryEventResponse, BeneficiaryResponse,
  DisbursementResponse, ProgramResponse, VoterLookupResponse,
} from '@/shared/api/types'
import { cn } from '@/lib/utils'
// The register owns identity resolution. A programme never re-implements it —
// `POST /voters/lookup` puts the identifier in the BODY and answers with who it
// found and where they are, in one call.
import { lookupVoter } from '@/features/voters/api'
import { UnitCascade, type CascadeValue } from '@/features/voters/UnitCascade'
import {
  changeBeneficiaryStatus, enrolBeneficiary, listBeneficiaries, listBeneficiaryEvents,
  listDisbursements, lookupBeneficiary, recordDisbursement, revealBeneficiaryContact,
  unlinkBeneficiaryVoter,
} from '../api'
import { Excluded } from '../parts'
import {
  BENEFICIARY_STATUS_ORDER, BENEFICIARY_TRANSITIONS, BeneficiaryStatusBadge,
  CHANNEL_ORDER, channelLabel,
  DROP_REASON_ORDER, DropReasonBadge, EventTypeBadge, beneficiaryStatusLabel, dropReasonLabel,
} from '../vocab'
import { DatePicker } from '@/components/erp/date-picker'
import type { Country } from 'react-phone-number-input'
import { PhoneInput, countryOf, DEFAULT_COUNTRY } from '@/components/erp/phone-input'

/**
 * The beneficiary list.
 *
 * ## A beneficiary is a PERSON, and `voterId` is genuinely nullable
 *
 * A bursary reaches a fifteen-year-old; a water grant reaches a grandmother who
 * never registered. Roughly a fifth of a real programme's beneficiaries have no
 * register row, and every figure on this screen that needed one says how many it
 * left out.
 *
 * ## No unmasked identifier is ever in this table, or in the URL
 *
 * The list carries `maskedPhone` and `maskedNationalId` and nothing else. The
 * full value comes from one audited endpoint, on an explicit click, and is never
 * cached. A digits-only search is refused before the request is made, because by
 * the time the server refuses it the value is already in the browser's history.
 */
export function BeneficiariesPanel({ program }: { program: ProgramResponse }) {
  const canEnrol = useCan('program:enrol')
  const canPii = useCan('program:read-pii')

  const [status, setStatus] = useState('')
  const [linked, setLinked] = useState('')
  const [draft, setDraft] = useState('')
  const debounced = useDebounced(draft, 300)

  const digits = debounced.replace(/[\s()-]/g, '')
  const looksLikeIdentifier = /^\+?\d{7,13}$/.test(digits)
  const q = looksLikeIdentifier ? '' : debounced

  const filters = useMemo(() => ({
    status: status || undefined,
    linked: linked || undefined,
    q: q || undefined,
  }), [status, linked, q])

  const page = useCursorPage<BeneficiaryResponse>(
    (cursor) => listBeneficiaries(program.id, filters, cursor),
    [program.id, JSON.stringify(filters)],
  )

  const [enrolling, setEnrolling] = useState(false)
  const [lookingUp, setLookingUp] = useState(false)
  const [subject, setSubject] = useState<BeneficiaryResponse | null>(null)

  const rows = page.rows
  const unlinked = rows.filter((r) => !r.voterId).length
  const noId = rows.filter((r) => !r.voterId && !r.maskedNationalId).length
  const pageScope = page.page > 0 || page.hasMore ? ' on this page' : ''

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            className="erp-input pl-7 h-8 w-52 text-erp-xs"
            placeholder="Search by name"
            aria-label="Search beneficiaries by name"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
        <select className="erp-input h-8 text-erp-xs" aria-label="Status"
          value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {BENEFICIARY_STATUS_ORDER.map((s) => (
            <option key={s} value={s}>{beneficiaryStatusLabel(s)}</option>
          ))}
        </select>
        <select className="erp-input h-8 text-erp-xs" aria-label="Register link"
          value={linked} onChange={(e) => setLinked(e.target.value)}>
          <option value="">Linked and unlinked</option>
          <option value="true">Linked to the register</option>
          <option value="false">Not linked</option>
        </select>
        {canEnrol && (
          <button type="button" className="erp-btn h-8 ml-auto" onClick={() => setEnrolling(true)}>
            <Plus className="h-3.5 w-3.5" /> Enrol
          </button>
        )}
      </div>

      {looksLikeIdentifier && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-erp-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
          <p>
            That looks like a national ID or a phone number. Identifiers must not appear in a
            URL — the search box refuses it rather than putting it in your address bar and in
            the server's access log.
          </p>
          <button type="button" className="erp-btn mt-2" onClick={() => setLookingUp(true)}>
            <IdCard className="h-3.5 w-3.5" /> Look this person up instead
          </button>
        </div>
      )}

      {/* Counted over THIS PAGE. The strip reads as the programme's exclusions,
          so it has to say which population it is describing — the programme-wide
          figures are on the funnel and the impact tabs, which ask the server. */}
      <Excluded items={[
        { count: unlinked, label: `not linked to the register${pageScope}`,
          hint: 'Any figure needing a voter — support, uplift, ward placement — excludes these.' },
        { count: noId, label: `with no national ID recorded${pageScope}`,
          hint: 'These cannot be compared across programmes, so the duplicate check cannot see them.' },
      ]} />

      <ErrorStrip message={page.error} />
      <ErpTable head={(
        <>
          <Th>Person</Th>
          <Th>Status</Th>
          <Th>Contact</Th>
          <Th>Enrolled</Th>
          <Th>By</Th>
          <Th>Support before</Th>
        </>
      )}>
        {page.loading && rows.length === 0 ? (
          <LoadingRows colSpan={6} />
        ) : rows.length === 0 ? (
          <EmptyRow colSpan={6} message="No beneficiaries match these filters." />
        ) : rows.map((b) => (
          // The beneficiary dialog is the only route to a status change, an
          // unlink and the audited PII reveal. Without a tab stop none of that
          // is reachable without a mouse.
          <tr
            key={b.id}
            className="erp-row cursor-pointer"
            onClick={() => setSubject(b)}
            // Not `role="button"` — see the programmes list for why. Keyboard
            // access without dismantling the row's semantics.
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubject(b) }
            }}
          >
            <Td>
              <div className="font-medium">{b.displayName ?? '—'}</div>
              <div className="text-erp-xs text-muted-foreground">
                {b.voterId
                  ? 'Linked to the register'
                  : 'Not on the register — recorded in the field'}
              </div>
            </Td>
            <Td>
              <BeneficiaryStatusBadge code={b.statusCode} />
              {b.dropReasonCode && <div className="mt-0.5"><DropReasonBadge code={b.dropReasonCode} /></div>}
            </Td>
            <Td className="text-erp-xs font-mono">
              {b.maskedPhone ?? <span className="text-muted-foreground font-sans">no phone</span>}
              {b.maskedNationalId && <div>{b.maskedNationalId}</div>}
            </Td>
            <Td className="text-erp-xs whitespace-nowrap">{b.enrolledAt ? fmtDate(b.enrolledAt) : '—'}</Td>
            <Td className="text-erp-xs">{b.enrolledByMobilizerName ?? '—'}</Td>
            <Td className="text-erp-xs tabular-nums">
              {/* NULL means nobody ever assessed them — not a support of zero.
                  Storing 0 for "never asked" is the defect this phase removes. */}
              {b.supportBefore === null || b.supportBefore === undefined
                ? <span className="text-muted-foreground">not assessed</span>
                : b.supportBefore}
            </Td>
          </tr>
        ))}
      </ErpTable>
      <CursorPager
        page={page.page} count={rows.length} hasMore={page.hasMore}
        canGoBack={page.canGoBack} onBack={page.back} onNext={page.next}
        loading={page.loading} unit="beneficiaries"
      />

      {lookingUp && <LookupDialog onClose={() => setLookingUp(false)} />}

      {enrolling && (
        <EnrolDialog
          program={program}
          onClose={() => setEnrolling(false)}
          onSaved={() => { setEnrolling(false); page.reload() }}
        />
      )}

      {subject && (
        <BeneficiaryDialog
          key={subject.id}
          program={program}
          beneficiary={subject}
          canPii={canPii}
          canEnrol={canEnrol}
          onClose={() => setSubject(null)}
          onChanged={(b) => { setSubject(b); page.reload() }}
        />
      )}
    </div>
  )
}

function EnrolDialog({ program, onClose, onSaved }: {
  program: ProgramResponse; onClose: () => void; onSaved: () => void
}) {
  const [mode, setMode] = useState<'linked' | 'unlinked'>('unlinked')

  // ── linked: find the person, do not ask for a uuid ──────────────────────
  const [lookupKind, setLookupKind] = useState<'nationalId' | 'phone'>('nationalId')
  const [lookupValue, setLookupValue] = useState('')
  const [looking, setLooking] = useState(false)
  const [found, setFound] = useState<VoterLookupResponse | null>(null)

  // ── unlinked ────────────────────────────────────────────────────────────
  const [fullName, setFullName] = useState('')
  const [nationalId, setNationalId] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneCountry, setPhoneCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [genderCode, setGenderCode] = useState('')
  const [birthYear, setBirthYear] = useState('')
  const [unit, setUnit] = useState<CascadeValue>({ unitId: undefined, path: [] })

  // ── what they were given, optionally, at the same moment ────────────────
  const [amount, setAmount] = useState('')
  const [channelCode, setChannelCode] = useState(program.deliveryChannelCode ?? 'MOBILE_MONEY')
  const [occurredOn, setOccurredOn] = useState(dayKey(new Date().toISOString()))

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [partial, setPartial] = useState<string | null>(null)

  const lookup = async () => {
    setLooking(true); setError(null); setFound(null)
    try {
      setFound(await lookupVoter(
        lookupKind === 'nationalId'
          ? { nationalId: lookupValue.trim() }
          : { phone: lookupValue.trim() },
      ))
    } catch (e) { setError(explain(e)) } finally { setLooking(false) }
  }

  const voterId = found?.alreadyRegistered ? found.existingVoterId ?? '' : ''

  const submit = async () => {
    setBusy(true); setError(null); setPartial(null)
    let created: BeneficiaryResponse
    try {
      created = await enrolBeneficiary(program.id, mode === 'linked'
        ? { voterId }
        : {
          fullName: fullName.trim(),
          nationalId: nationalId.trim() || null,
          phone: phone || null,
          phoneCountry: phone ? countryOf(phone, phoneCountry) : null,
          genderCode: genderCode || null,
          birthYear: birthYear ? Number(birthYear) : null,
          // REQUIRED for an unlinked person. A linked one inherits the voter's
          // polling station; an unlinked one has no other source of geography, so
          // the server refuses without it — and this dialog used not to send it
          // at all, which made the whole unlinked path fail.
          unitId: unit.unitId,
        })
    } catch (e) { setError(explain(e)); setBusy(false); return }

    // TWO writes, and the second can fail on its own. A programme with the wrong
    // currency, a channel the programme does not use — any of it 4xxs after the
    // person is already enrolled. Saying "enrolled, payment not recorded" is the
    // only honest report; closing the dialog on a green tick would leave somebody
    // believing a payment exists that the ledger has never heard of.
    if (amount.trim()) {
      try {
        await recordDisbursement(program.id, {
          beneficiaryId: created.id,
          amount: Number(amount),
          currency: program.currency,
          channelCode,
          occurredOn,
        })
      } catch (e) {
        setPartial(`${created.displayName ?? 'The beneficiary'} was enrolled, but the payment was `
          + `not recorded — ${explain(e)} Record it from the Money tab.`)
        setBusy(false)
        return
      }
    }
    setBusy(false)
    onSaved()
  }

  const ready = mode === 'linked'
    ? Boolean(voterId)
    : Boolean(fullName.trim() && unit.unitId)

  return (
    <Modal open onClose={onClose} width="lg" title="Enrol a beneficiary" footer={(
      <>
        <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="erp-btn-primary" onClick={submit} disabled={busy || !ready}>
          {busy ? 'Enrolling…' : 'Enrol'}
        </button>
      </>
    )}>
      <div className="space-y-3">
        <ErrorStrip message={error} />
        {/* EITHER a register link OR the person's own details — never both.
            Two copies of a name is two names that disagree the day somebody
            marries, and the server enforces it as a constraint. */}
        {partial && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-erp-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200"
            role="alert">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
            <span>{partial}</span>
          </div>
        )}

        <div className="flex gap-2" role="radiogroup" aria-label="Identity">
          <button type="button" role="radio" aria-checked={mode === 'unlinked'}
            className={mode === 'unlinked' ? 'erp-btn-primary h-8' : 'erp-btn h-8'}
            onClick={() => setMode('unlinked')}>Not on the register</button>
          <button type="button" role="radio" aria-checked={mode === 'linked'}
            className={mode === 'linked' ? 'erp-btn-primary h-8' : 'erp-btn h-8'}
            onClick={() => setMode('linked')}>Linked to a voter</button>
        </div>

        {mode === 'linked' ? (
          <>
            <Note tone="info">
              A linked beneficiary takes their name, gender and age from the register. Nothing is
              copied onto this row, so a correction there is a correction here.
            </Note>

            {/* NOT a "Voter id" text box.
                Nobody carries a uuid. A caseworker holds a national ID card, types
                the number, and the first version of this dialog sent that string
                into a `UUID` field — Jackson could not parse it and the server
                answered "Malformed request body", which tells the caseworker
                nothing about what they did.
                The identifier goes in the BODY of the lookup, never a URL. */}
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Find by">
                <select className="erp-input" value={lookupKind}
                  onChange={(e) => { setLookupKind(e.target.value as typeof lookupKind); setFound(null) }}>
                  <option value="nationalId">National ID</option>
                  <option value="phone">Phone number</option>
                </select>
              </Field>
              <Field label={lookupKind === 'nationalId' ? 'National ID' : 'Phone number'} required>
                <input className="erp-input" value={lookupValue} autoFocus
                  onChange={(e) => { setLookupValue(e.target.value); setFound(null) }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && lookupValue.trim()) void lookup() }} />
              </Field>
              <button type="button" className="erp-btn h-9" disabled={!lookupValue.trim() || looking}
                onClick={() => void lookup()}>
                <Search className="h-3.5 w-3.5" /> {looking ? 'Searching…' : 'Find'}
              </button>
            </div>

            {found && (found.alreadyRegistered ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/50 dark:bg-emerald-950/40">
                <div className="text-erp-sm font-medium text-emerald-900 dark:text-emerald-200">
                  {found.displayName ?? 'On the register'}
                </div>
                <div className="text-erp-xs text-emerald-900/80 dark:text-emerald-200/80">
                  {[found.wardName, found.constituencyName, found.countyName]
                    .filter(Boolean).join(' · ') || 'Placement not recorded'}
                  {found.pollingStationName ? ` · ${found.pollingStationName}` : ''}
                </div>
                {found.registrationStatusCode && (
                  <div className="text-erp-xs text-emerald-900/80 dark:text-emerald-200/80 mt-0.5">
                    {humanise(found.registrationStatusCode)}
                  </div>
                )}
                <p className="text-erp-xs text-muted-foreground mt-1.5">
                  Enrolling links to this record. The beneficiary's unit defaults to their
                  polling station.
                </p>
              </div>
            ) : (
              <Note tone="bad">
                {found.source === 'ECITIZEN_IPRS'
                  ? 'eCitizen knows this person but your register does not. Add them to the register first — a beneficiary can only link to a voter record that exists here.'
                  : 'Nobody on the register matches that. Enrol them as "Not on the register" instead, or check the number.'}
              </Note>
            ))}
          </>
        ) : (
          <>
            <Note tone="info">
              For somebody the register does not have — a child on a bursary, a grandmother who
              never registered. Their national ID, if they give one, is encrypted and only ever
              shown masked.
            </Note>
            <Field label="Full name" required>
              <input className="erp-input w-full" value={fullName} maxLength={160}
                onChange={(e) => setFullName(e.target.value)} />
            </Field>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
              <Field label="National ID" hint="Optional. Without one, this person cannot be checked against other programmes.">
                <input className="erp-input w-full" value={nationalId}
                  onChange={(e) => setNationalId(e.target.value)} />
              </Field>
              <Field label="Phone" hint="Optional. Stored encrypted; shown masked everywhere.">
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  country={phoneCountry}
                  onCountryChange={setPhoneCountry}
                />
              </Field>
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
              <Field label="Gender" hint="Optional. Feeds the equity index.">
                <select className="erp-input w-full" value={genderCode}
                  onChange={(e) => setGenderCode(e.target.value)}>
                  <option value="">Not recorded</option>
                  <option value="FEMALE">Female</option>
                  <option value="MALE">Male</option>
                  <option value="OTHER">Other</option>
                </select>
              </Field>
              <Field label="Year of birth" hint="Optional. The year only — never a full date.">
                <input className="erp-input w-full" inputMode="numeric" maxLength={4}
                  value={birthYear} onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, ''))} />
              </Field>
            </div>
            <Field label="Where" required
              hint="An unlinked person has no polling station to inherit, so their unit has to be stated.">
              <UnitCascade value={unit} onChange={setUnit} required />
            </Field>
          </>
        )}

        {/* ── what they were given, optional ──────────────────────────────
            NOT a column on the beneficiary. Money lives in the disbursement
            ledger and nowhere else — `program.spentAmount` is a trigger-maintained
            rollup of it, and a second amount on this row is a second number that
            nothing keeps in agreement with the first. That is the failure this
            product was rebuilt to remove.
            So an amount here writes a LEDGER ENTRY attributed to this person, and
            it is the same entry the Money tab shows. */}
        <div className="border-t border-border pt-3">
          <div className="text-erp-xs font-medium text-muted-foreground mb-1.5">
            Given at enrolment <span className="font-normal">— optional</span>
          </div>
          {program.currency ? (
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              <Field label={`Amount (${program.currency})`}
                hint="Leave blank if nothing has been handed over yet.">
                <input className="erp-input w-full" inputMode="decimal" value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                  placeholder="0.00" />
              </Field>
              <Field label="Channel">
                <select className="erp-input w-full" value={channelCode}
                  onChange={(e) => setChannelCode(e.target.value as typeof channelCode)}
                  disabled={!amount.trim()}>
                  {CHANNEL_ORDER.map((c) => (
                    <option key={c} value={c}>{channelLabel(c)}</option>
                  ))}
                </select>
              </Field>
              <Field label="On">
                <DatePicker
                  value={occurredOn}
                  onChange={setOccurredOn}
                  disabled={!amount.trim()}
                  className="w-full"
                />
              </Field>
            </div>
          ) : (
            <p className="text-erp-xs text-muted-foreground">
              This programme has no currency set, so nothing can be disbursed against it. Set one
              on the programme first — the server refuses a payment without it rather than
              guessing.
            </p>
          )}
          {amount.trim() && (
            <p className="mt-1.5 text-erp-xs text-muted-foreground">
              This writes a row in the programme's ledger against this person. Corrections are a
              reversal, never an edit.
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}

function BeneficiaryDialog({ program, beneficiary, canPii, canEnrol, onClose, onChanged }: {
  program: ProgramResponse
  beneficiary: BeneficiaryResponse
  canPii: boolean
  canEnrol: boolean
  onClose: () => void
  onChanged: (b: BeneficiaryResponse) => void
}) {
  const [events, setEvents] = useState<BeneficiaryEventResponse[] | null>(null)
  const [contact, setContact] = useState<BeneficiaryContactResponse | null>(null)
  const [revealing, setRevealing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [moving, setMoving] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [unlinking, setUnlinking] = useState(false)
  const [unlinkName, setUnlinkName] = useState('')

  useEffect(() => {
    let alive = true
    listBeneficiaryEvents(beneficiary.id)
      .then((r) => { if (alive) setEvents(r) })
      .catch(() => { if (alive) setEvents([]) })
    return () => { alive = false }
  }, [beneficiary.id])

  const reveal = async () => {
    setRevealing(true); setError(null)
    try { setContact(await revealBeneficiaryContact(beneficiary.id)) }
    catch (e) { setError(explain(e)) } finally { setRevealing(false) }
  }

  const move = async (to: string) => {
    setBusy(true); setError(null)
    try {
      const next = await changeBeneficiaryStatus(beneficiary.id, to, reason || undefined)
      onChanged(next); setMoving(null); setReason('')
      listBeneficiaryEvents(beneficiary.id).then(setEvents).catch(() => {})
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  const doUnlink = async () => {
    setBusy(true); setError(null)
    try {
      const next = await unlinkBeneficiaryVoter(beneficiary.id, unlinkName)
      onChanged(next); setUnlinking(false); setUnlinkName('')
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  const transitions = BENEFICIARY_TRANSITIONS[beneficiary.statusCode] ?? []
  // The reason belongs to the move being MADE, not to the status being left.
  // `|| statusCode === 'DROPPED'` demanded one when reinstating somebody, and
  // the only list on offer was the DROP reasons — so a reinstatement was stamped
  // with "moved away", permanently, in an append-only event log.
  const needsReason = moving === 'DROPPED'

  return (
    <Modal open onClose={onClose} width="lg" title={beneficiary.displayName ?? "Beneficiary"}>
      <div className="space-y-4">
        <ErrorStrip message={error} />

        <div className="flex flex-wrap items-center gap-2">
          <BeneficiaryStatusBadge code={beneficiary.statusCode} />
          {beneficiary.voterId
            ? <span className="text-erp-xs text-muted-foreground">Linked to the register</span>
            : <span className="text-erp-xs text-muted-foreground">Recorded in the field</span>}
          {beneficiary.matchedRuleVersion && (
            <span className="text-erp-xs text-muted-foreground">
              matched rule v{beneficiary.matchedRuleVersion}
            </span>
          )}
        </div>

        <section className="space-y-1">
          <h4 className="text-erp-sm font-medium">Contact</h4>
          <p className="text-erp-xs font-mono">
            {beneficiary.maskedPhone ?? 'no phone recorded'}
            {beneficiary.maskedNationalId ? ` · ${beneficiary.maskedNationalId}` : ''}
          </p>
          {canPii ? (
            contact ? (
              <div className="rounded-md border border-amber-300 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-2">
                <p className="text-erp-sm font-mono">{contact.phone ?? 'no phone'}</p>
                {contact.nationalId && <p className="text-erp-sm font-mono">{contact.nationalId}</p>}
                <p className="text-erp-xs text-amber-800 dark:text-amber-300">
                  This reveal was recorded in the audit log against your account.
                  {beneficiary.voterId && !contact.nationalId &&
                    ' A linked beneficiary has no stored national ID — the register holds it, behind its own authority.'}
                </p>
              </div>
            ) : (
              <button type="button" className="erp-btn h-7 text-erp-xs" onClick={reveal} disabled={revealing}>
                <Eye className="h-3.5 w-3.5" /> {revealing ? 'Revealing…' : 'Reveal full contact (audited)'}
              </button>
            )
          ) : (
            <p className="text-erp-xs text-muted-foreground">
              Revealing a full phone or national ID needs the <code>program:read-pii</code> authority.
            </p>
          )}
        </section>

        <BeneficiaryPayments program={program} beneficiary={beneficiary} canEnrol={canEnrol} />

        <section className="space-y-1">
          <h4 className="text-erp-sm font-medium">Matched on</h4>
          <p className="text-erp-xs text-muted-foreground">
            {(beneficiary.matchedCriteria ?? []).length
              ? beneficiary.matchedCriteria.join(', ')
              : 'No criteria recorded'}
            {' — '}field names only. The values belong to the register and are read from it.
          </p>
        </section>

        {canEnrol && (
          <section className="space-y-2">
            <h4 className="text-erp-sm font-medium">Move</h4>
            <div className="flex flex-wrap gap-2">
              {transitions.map((to) => (
                <button key={to} type="button" className="erp-btn h-7 text-erp-xs"
                  onClick={() => setMoving(to)} disabled={busy}>
                  {beneficiaryStatusLabel(to)}
                </button>
              ))}
              {beneficiary.voterId && (
                <button type="button" className="erp-btn h-7 text-erp-xs"
                  onClick={() => setUnlinking(true)} disabled={busy}>
                  <UserMinus className="h-3.5 w-3.5" /> Unlink from register
                </button>
              )}
              {!beneficiary.voterId && (
                <span className="inline-flex items-center gap-1 text-erp-xs text-muted-foreground">
                  <UserPlus className="h-3.5 w-3.5" /> Linking is done from the register side
                </span>
              )}
            </div>
            {beneficiary.statusCode === 'DELIVERED' && (
              <p className="text-erp-xs text-muted-foreground">
                Delivering again is expected — a weekly water run delivers to the same person many
                times. Only the first stamps the delivery date.
              </p>
            )}
            {moving && (
              <div className="rounded-md border border-border p-2 space-y-2">
                <p className="text-erp-xs">
                  Move to <strong>{beneficiaryStatusLabel(moving)}</strong>
                  {needsReason ? ' — a reason is required and is recorded permanently.' : '.'}
                </p>
                {needsReason && (
                  <select className="erp-input h-8 text-erp-xs" aria-label="Reason"
                    value={reason} onChange={(e) => setReason(e.target.value)}>
                    <option value="">Choose a reason…</option>
                    {DROP_REASON_ORDER.map((r) => (
                      <option key={r} value={r}>{dropReasonLabel(r)}</option>
                    ))}
                  </select>
                )}
                <div className="flex gap-2">
                  <button type="button" className="erp-btn h-7 text-erp-xs"
                    onClick={() => { setMoving(null); setReason('') }}>Cancel</button>
                  <button type="button" className="erp-btn-primary h-7 text-erp-xs"
                    disabled={busy || (needsReason && !reason)}
                    onClick={() => move(moving)}>Confirm</button>
                </div>
              </div>
            )}
            {unlinking && (
              <div className="rounded-md border border-border p-2 space-y-2">
                {/* The name is REQUIRED and not as an API preference: unlinking
                    clears the voter link, and the identity constraint then
                    demands a name that linking had cleared. */}
                <p className="text-erp-xs">
                  Unlinking needs this person's name — the register was supplying it, and there is
                  nothing to fall back on.
                </p>
                <input className="erp-input h-8 text-erp-xs" placeholder="Full name"
                  aria-label="Full name" value={unlinkName}
                  onChange={(e) => setUnlinkName(e.target.value)} />
                <div className="flex gap-2">
                  <button type="button" className="erp-btn h-7 text-erp-xs"
                    onClick={() => setUnlinking(false)}>Cancel</button>
                  <button type="button" className="erp-btn-primary h-7 text-erp-xs"
                    disabled={busy || !unlinkName} onClick={doUnlink}>Unlink</button>
                </div>
              </div>
            )}
          </section>
        )}

        <section className="space-y-1">
          <h4 className="text-erp-sm font-medium">History</h4>
          {!events ? <p className="text-erp-xs text-muted-foreground">Loading…</p>
            : events.length === 0 ? <p className="text-erp-xs text-muted-foreground">No events.</p>
            : (
              <ul className="space-y-1">
                {events.map((e) => (
                  <li key={e.id} className="flex flex-wrap items-center gap-2 text-erp-xs">
                    <EventTypeBadge code={e.eventTypeCode} />
                    <span className="text-muted-foreground">{fmtDate(e.occurredAt)}</span>
                    {e.actorLabel && <span className="text-muted-foreground">{e.actorLabel}</span>}
                    {e.reasonCode && <span>{dropReasonLabel(e.reasonCode)}</span>}
                    {e.note && <span className="text-muted-foreground">— {e.note}</span>}
                  </li>
                ))}
              </ul>
            )}
          <p className="text-erp-xs text-muted-foreground italic">
            Append-only. Nothing here can be edited or removed, including by an administrator.
          </p>
        </section>
      </div>
    </Modal>
  )
}

export const BeneficiaryCount = fmtNumber


/**
 * The lookup the refusal points at.
 *
 * The search box turns an identifier away because a national ID in a query
 * string lands in browser history, in a `Referer` header and in an access log.
 * That refusal was, for a while, pointing at a control that did not exist —
 * `lookupBeneficiary` was exported and never called from anywhere. A refusal
 * that names a nonexistent alternative is worse than no refusal: the reader
 * concludes the product is broken and goes looking for a way round it.
 *
 * The value travels in the BODY. A national ID additionally needs
 * `program:read-pii` and writes an audit row server-side — so a 403 here is the
 * system working, and the copy says which authority is missing rather than
 * pretending the person does not exist.
 *
 * An unknown value returns an EMPTY LIST, never a 404: a 404 would answer the
 * question "did this person take money from the campaign" for anybody who could
 * reach the endpoint.
 */
function LookupDialog({ onClose }: { onClose: () => void }) {
  const [kind, setKind] = useState<'phone' | 'nationalId'>('phone')
  const [value, setValue] = useState('')
  const [rows, setRows] = useState<BeneficiaryResponse[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async () => {
    setBusy(true); setError(null); setRows(null)
    try {
      setRows(await lookupBeneficiary(
        kind === 'phone' ? { phone: value.trim() } : { nationalId: value.trim() },
      ))
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} width="lg" title="Look up a person"
      description="The value is sent in the request body, so it never reaches a URL.">
      <div className="space-y-3">
        <ErrorStrip message={error} />
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Search by">
            <select className="erp-input" value={kind}
              onChange={(e) => { setKind(e.target.value as typeof kind); setRows(null) }}>
              <option value="phone">Phone number</option>
              <option value="nationalId">National ID</option>
            </select>
          </Field>
          <Field label="Value">
            <input className="erp-input" value={value} onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && value.trim()) void run() }} />
          </Field>
          <button type="button" className="erp-btn-primary h-9" disabled={!value.trim() || busy}
            onClick={() => void run()}>
            {busy ? 'Searching…' : 'Search'}
          </button>
        </div>

        {kind === 'nationalId' && (
          <Note tone="info">
            A national-ID lookup needs the PII authority and is written to the audit log with
            your name against it. The ID itself is not recorded — only that a lookup happened.
          </Note>
        )}

        {rows && (rows.length === 0 ? (
          <p className="text-erp-sm text-muted-foreground">
            No beneficiary in your scope matches that value. That is not the same as
            "this person is not in the system" — it is the only answer this endpoint gives.
          </p>
        ) : (
          <ErpTable head={(
            <>
              <Th>Person</Th>
              <Th>Programme</Th>
              <Th>Status</Th>
            </>
          )}>
            {rows.map((b) => (
              <tr key={b.id}>
                <Td>{b.displayName ?? '—'}</Td>
                <Td className="text-erp-xs">{b.programName ?? b.programId}</Td>
                <Td><BeneficiaryStatusBadge code={b.statusCode} /></Td>
              </tr>
            ))}
          </ErpTable>
        ))}
      </div>
    </Modal>
  )
}


/**
 * What this person has been given — read from the LEDGER, not from a field.
 *
 * `BeneficiaryResponse` carries no money at all, deliberately. A programme's
 * `spentAmount` is a trigger-maintained rollup of `program_disbursement`, and an
 * amount stored on the beneficiary row as well would be a second number that
 * nothing keeps in agreement with the first — five money columns that disagreed
 * is the specific thing this product was rebuilt to stop doing.
 *
 * So "how much have they been given" is a filtered read of the ledger, and
 * recording a payment writes one row there. The Money tab shows the same rows.
 */
function BeneficiaryPayments({ program, beneficiary, canEnrol }: {
  program: ProgramResponse
  beneficiary: BeneficiaryResponse
  canEnrol: boolean
}) {
  const canDisburse = useCan('program:disburse')
  const [rows, setRows] = useState<DisbursementResponse[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [amount, setAmount] = useState('')
  const [channelCode, setChannelCode] = useState(program.deliveryChannelCode ?? 'MOBILE_MONEY')
  const [occurredOn, setOccurredOn] = useState(dayKey(new Date().toISOString()))
  const [busy, setBusy] = useState(false)

  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const load = () => listDisbursements(program.id, { beneficiaryId: beneficiary.id })
    .then((page) => { if (alive.current) { setRows(page.content); setError(null) } })
    .catch((e) => { if (alive.current) { setRows(null); setError(explain(e)) } })

  useEffect(() => { void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program.id, beneficiary.id])

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      await recordDisbursement(program.id, {
        beneficiaryId: beneficiary.id,
        amount: Number(amount),
        currency: program.currency,
        channelCode,
        occurredOn,
      })
      if (!alive.current) return
      setAmount(''); setAdding(false)
      await load()
    } catch (e) { if (alive.current) setError(explain(e)) }
    finally { if (alive.current) setBusy(false) }
  }

  // Reversals are NEGATIVE rows in the same ledger, so a plain sum is the net —
  // which is the number somebody means by "how much have they been given".
  const net = (rows ?? []).reduce((n, d) => n + Number(d.amount ?? 0), 0)
  const reversals = (rows ?? []).filter((d) => Number(d.amount ?? 0) < 0).length

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-erp-sm font-medium">Given</h4>
        {canEnrol && canDisburse && program.currency && (
          <button type="button" className="erp-btn h-7 text-erp-xs"
            onClick={() => setAdding((a) => !a)} aria-expanded={adding}>
            <Plus className="h-3.5 w-3.5" /> {adding ? 'Cancel' : 'Record a payment'}
          </button>
        )}
      </div>

      <ErrorStrip message={error} />

      {rows === null && !error ? (
        <p className="text-erp-xs text-muted-foreground">Loading the ledger…</p>
      ) : rows && rows.length === 0 ? (
        <p className="text-erp-xs text-muted-foreground">
          Nothing recorded against this person yet. Enrolment is not a payment.
        </p>
      ) : rows && (
        <>
          <p className="text-erp-lg font-semibold tabular-nums">
            {program.currency ? `${program.currency} ` : ''}{fmtNumber(Math.round(net))}
          </p>
          <ul className="space-y-0.5">
            {rows.map((d) => (
              <li key={d.id} className="flex items-baseline gap-2 text-erp-xs">
                <span className="text-muted-foreground">{fmtDate(d.occurredOn)}</span>
                <span>{channelLabel(d.channelCode)}</span>
                <span className={cn('ml-auto tabular-nums',
                  Number(d.amount) < 0 && 'text-muted-foreground')}>
                  {d.currency} {fmtNumber(Number(d.amount))}
                </span>
              </li>
            ))}
          </ul>
          {reversals > 0 && (
            <p className="text-erp-xs text-muted-foreground">
              Net of {reversals} {reversals === 1 ? 'reversal' : 'reversals'} — a correction is a
              negative row, never an edit.
            </p>
          )}
        </>
      )}

      {adding && program.currency && (
        <div className="rounded-md border border-border p-2.5 space-y-2">
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
            <Field label={`Amount (${program.currency})`}>
              <input className="erp-input w-full" inputMode="decimal" value={amount} autoFocus
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} />
            </Field>
            <Field label="Channel">
              <select className="erp-input w-full" value={channelCode}
                onChange={(e) => setChannelCode(e.target.value as typeof channelCode)}>
                {CHANNEL_ORDER.map((c) => <option key={c} value={c}>{channelLabel(c)}</option>)}
              </select>
            </Field>
            <Field label="On">
              <DatePicker value={occurredOn} onChange={setOccurredOn} className="w-full" />
            </Field>
          </div>
          <button type="button" className="erp-btn-primary h-8 text-erp-xs"
            disabled={busy || !amount.trim() || Number(amount) <= 0}
            onClick={() => void submit()}>
            {busy ? 'Recording…' : 'Record'}
          </button>
          <p className="text-erp-xs text-muted-foreground">
            The amount is positive. A correction is a reversal from the Money tab, which writes
            its own negative row — there is no way to edit one afterwards.
          </p>
        </div>
      )}
    </section>
  )
}
